import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import {
    KeyRound, Webhook, BookOpen, Network, Copy, Plus, Trash2, RefreshCcw,
    Download, ExternalLink, CheckCircle2, XCircle, Loader2, Eye, EyeOff,
    Code2, Terminal, Globe2, ShieldCheck, Zap, PlugZap, ChevronDown,
    ChevronUp, ArrowLeftRight, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// ── Helpers ───────────────────────────────────────────────────────────────────

const METHOD_COLOR = {
    GET:    'bg-blue-100 text-blue-800 border-blue-300',
    POST:   'bg-green-100 text-green-800 border-green-300',
    PUT:    'bg-amber-100 text-amber-800 border-amber-300',
    DELETE: 'bg-red-100 text-red-800 border-red-300',
    PATCH:  'bg-purple-100 text-purple-800 border-purple-300',
};

const copyText = (txt) => {
    if (navigator?.clipboard) navigator.clipboard.writeText(txt);
    toast.success('Copiado');
};

const ALL_EVENTS = [
    'ad.created', 'ad.updated',
    'user.registered', 'user.updated',
    'wallet.topup', 'wallet.withdraw', 'wallet.transfer',
    'order.created', 'order.completed',
    'connect.test',
];

// ── Code example generator ────────────────────────────────────────────────────

function buildCodeExample(lang, method, path, body, baseUrl) {
    const url = `${baseUrl}/api${path}`;
    if (lang === 'curl') {
        const bodyStr = body ? `\\\n  -d '${JSON.stringify(body, null, 2)}'` : '';
        return `curl -X ${method} "${url}" \\
  -H "Authorization: Bearer ok_live_YOUR_KEY" \\
  -H "X-Platform-Code: OK08047AB3XYZ1" \\
  -H "Content-Type: application/json" ${bodyStr}`;
    }
    if (lang === 'js') {
        const bodyStr = body ? `\n  body: JSON.stringify(${JSON.stringify(body, null, 4)}),` : '';
        return `const res = await fetch("${url}", {
  method: "${method}",
  headers: {
    "Authorization": "Bearer ok_live_YOUR_KEY",
    "X-Platform-Code": "OK08047AB3XYZ1",
    "Content-Type": "application/json",
  },${bodyStr}
});
const data = await res.json();
console.log(data);`;
    }
    if (lang === 'python') {
        const bodyStr = body ? `\ndata = ${JSON.stringify(body, null, 4)}` : '\ndata = None';
        return `import requests

BASE = "${url}"
HEADERS = {
    "Authorization": "Bearer ok_live_YOUR_KEY",
    "X-Platform-Code": "OK08047AB3XYZ1",
    "Content-Type": "application/json",
}
${bodyStr}
r = requests.${method.toLowerCase()}(BASE, headers=HEADERS${body ? ', json=data' : ''})
print(r.json())`;
    }
    return '';
}

// ── Endpoint detail card ──────────────────────────────────────────────────────

const EndpointCard = ({ method, path, op, baseUrl }) => {
    const [open, setOpen] = useState(false);
    const [lang, setLang] = useState('curl');

    const bodySchema = op.requestBody?.content?.['application/json']?.schema;
    const bodyExample = bodySchema?.properties
        ? Object.fromEntries(Object.entries(bodySchema.properties).map(([k, v]) => [k, v.example ?? (v.type === 'boolean' ? false : v.type === 'integer' ? 0 : v.type === 'array' ? [] : '')]))
        : null;

    const code = buildCodeExample(lang, method.toUpperCase(), path, bodyExample, baseUrl);

    return (
        <div className="border rounded-xl overflow-hidden">
            <button
                className="w-full flex items-center gap-3 p-3 hover:bg-secondary/40 text-left"
                onClick={() => setOpen(o => !o)}
            >
                <Badge variant="outline" className={`text-[10px] font-mono shrink-0 ${METHOD_COLOR[method.toUpperCase()] || ''}`}>
                    {method.toUpperCase()}
                </Badge>
                <code className="text-sm font-mono flex-1 truncate">{path}</code>
                <span className="text-xs text-muted-foreground truncate hidden sm:block">{op.summary}</span>
                {open ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
            </button>
            {open && (
                <div className="border-t bg-secondary/20 p-4 space-y-4">
                    {op.description && (
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{op.description}</p>
                    )}

                    {/* Parameters */}
                    {(op.parameters || []).filter(p => p.in !== 'header' || p.name === 'X-Platform-Code').length > 0 && (
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Parámetros</div>
                            <div className="space-y-1">
                                {op.parameters.map(p => (
                                    <div key={p.name} className="flex items-center gap-2 text-xs">
                                        <code className="font-mono bg-secondary px-1.5 py-0.5 rounded">{p.name}</code>
                                        <Badge variant="outline" className="text-[9px]">{p.in}</Badge>
                                        {p.required && <Badge className="bg-red-100 text-red-700 text-[9px]">requerido</Badge>}
                                        <span className="text-muted-foreground">{p.description || p.schema?.type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Body schema */}
                    {bodySchema?.properties && (
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Body (JSON)</div>
                            <div className="space-y-1">
                                {Object.entries(bodySchema.properties).map(([k, v]) => (
                                    <div key={k} className="flex items-center gap-2 text-xs">
                                        <code className="font-mono bg-secondary px-1.5 py-0.5 rounded">{k}</code>
                                        <span className="text-muted-foreground">{v.type || 'object'}</span>
                                        {(bodySchema.required || []).includes(k) && <Badge className="bg-red-100 text-red-700 text-[9px]">requerido</Badge>}
                                        {v.description && <span className="text-muted-foreground">— {v.description}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Code example */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ejemplo de código</div>
                            <div className="flex gap-1 ml-auto">
                                {['curl', 'js', 'python'].map(l => (
                                    <button key={l} onClick={() => setLang(l)}
                                        className={`text-[10px] px-2 py-0.5 rounded border ${lang === l ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
                                        {l === 'js' ? 'JavaScript' : l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <pre className="bg-neutral-900 text-neutral-100 text-xs rounded-lg p-3 overflow-x-auto leading-relaxed">{code}</pre>
                            <button onClick={() => copyText(code)}
                                className="absolute top-2 right-2 p-1.5 rounded bg-neutral-700 hover:bg-neutral-600 text-neutral-300">
                                <Copy className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Docs Tab ──────────────────────────────────────────────────────────────────

const DocsTab = () => {
    const [spec, setSpec] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTag, setActiveTag] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        api.get('/admin/api-spec/openapi').then(({ data }) => {
            setSpec(data);
            setActiveTag(data.tags?.[0]?.name || null);
        }).catch(() => toast.error('Error al cargar spec')).finally(() => setLoading(false));
    }, []);

    const download = async (format) => {
        setDownloading(format);
        try {
            const endpoint = format === 'openapi' ? '/admin/api-spec/openapi' : '/admin/api-spec/postman';
            const { data } = await api.get(endpoint);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'openapi' ? 'oktelovendo-openapi.json' : 'oktelovendo-postman.json';
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Descargado');
        } catch { toast.error('Error al descargar'); }
        finally { setDownloading(null); }
    };

    if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="h-4 w-4 animate-spin" />Cargando documentación…</div>;
    if (!spec) return <div className="text-muted-foreground">No se pudo cargar la documentación.</div>;

    const baseUrl = spec.servers?.[0]?.url?.replace('/api', '') || window.location.origin;

    // Group paths by tag
    const tagPaths = {};
    for (const [path, methods] of Object.entries(spec.paths || {})) {
        for (const [method, op] of Object.entries(methods)) {
            const tag = op.tags?.[0] || 'Misc';
            if (!tagPaths[tag]) tagPaths[tag] = [];
            tagPaths[tag].push({ path, method, op });
        }
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="rounded-2xl border bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-5">
                <div className="flex items-center gap-3 mb-2">
                    <BookOpen className="h-6 w-6 text-red-600" />
                    <div>
                        <div className="font-bold text-lg">{spec.info?.title}</div>
                        <div className="text-xs text-muted-foreground">v{spec.info?.version} · OpenAPI 3.0</div>
                    </div>
                    <div className="ml-auto flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => download('openapi')} disabled={downloading === 'openapi'}>
                            {downloading === 'openapi' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                            OpenAPI JSON
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => download('postman')} disabled={downloading === 'postman'}>
                            {downloading === 'postman' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                            Postman v2.1
                        </Button>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{spec.info?.description}</p>
            </div>

            {/* Auth box */}
            <div className="rounded-xl border bg-card p-4">
                <div className="font-semibold text-sm flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />Autenticación
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-secondary/50 p-3">
                        <div className="font-medium mb-1">API Key</div>
                        <code className="text-xs bg-secondary rounded px-2 py-0.5 block">Authorization: Bearer ok_live_…</code>
                        <div className="text-xs text-muted-foreground mt-1">Generada en el panel → Claves API. Sin expiración por defecto.</div>
                    </div>
                    <div className="rounded-lg bg-secondary/50 p-3">
                        <div className="font-medium mb-1">JWT (sesión de usuario)</div>
                        <code className="text-xs bg-secondary rounded px-2 py-0.5 block">Authorization: Bearer &lt;jwt&gt;</code>
                        <div className="text-xs text-muted-foreground mt-1">Obtenido en <code className="font-mono">POST /api/auth/login</code>.</div>
                    </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                    Los endpoints de Connect también requieren el header <code className="font-mono bg-secondary px-1.5 py-0.5 rounded">X-Platform-Code</code>.
                </div>
            </div>

            {/* Webhook signature box */}
            <div className="rounded-xl border bg-card p-4">
                <div className="font-semibold text-sm flex items-center gap-2 mb-2">
                    <Webhook className="h-4 w-4 text-blue-600" />Firma HMAC de Webhooks
                </div>
                <p className="text-sm text-muted-foreground mb-2">Todos los webhooks salientes incluyen el header <code className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">X-OkConnect-Signature</code> con una firma HMAC-SHA256 del body.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    {[
                        { lang: 'Python', code: 'import hmac, hashlib\nsig = hmac.new(secret.encode(), body, hashlib.sha256).hexdigest()' },
                        { lang: 'Node.js', code: "const sig = crypto.createHmac('sha256', secret).update(body).digest('hex');" },
                        { lang: 'PHP', code: '$sig = hash_hmac("sha256", $body, $secret);' },
                    ].map(({ lang: l, code }) => (
                        <div key={l}>
                            <div className="font-semibold mb-1">{l}</div>
                            <pre className="bg-neutral-900 text-neutral-200 rounded p-2 overflow-x-auto">{code}</pre>
                        </div>
                    ))}
                </div>
            </div>

            {/* Endpoint reference */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Tag sidebar */}
                <div className="space-y-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Secciones</div>
                    {(spec.tags || []).map(t => (
                        <button key={t.name}
                            onClick={() => setActiveTag(t.name)}
                            className={`w-full text-left text-sm px-3 py-1.5 rounded-lg transition-colors ${activeTag === t.name ? 'bg-red-600 text-white font-medium' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
                            {t.name}
                        </button>
                    ))}
                </div>

                {/* Endpoint list */}
                <div className="lg:col-span-3 space-y-3">
                    {activeTag && (
                        <>
                            <div className="font-semibold">{activeTag}</div>
                            <div className="text-xs text-muted-foreground">
                                {spec.tags?.find(t => t.name === activeTag)?.description}
                            </div>
                            <div className="space-y-2">
                                {(tagPaths[activeTag] || []).map(({ path, method, op }, i) => (
                                    <EndpointCard key={`${method}${path}${i}`} method={method} path={path} op={op} baseUrl={baseUrl} />
                                ))}
                                {!(tagPaths[activeTag] || []).length && (
                                    <div className="text-sm text-muted-foreground py-4 text-center">Sin endpoints en esta sección.</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Webhooks Tab ──────────────────────────────────────────────────────────────

const WebhooksTab = ({ platforms }) => {
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ platform_id: '', target_url: '', events: [], description: '' });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);
    const [platformFilter, setPlatformFilter] = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = platformFilter !== 'all' ? `?platform_id=${platformFilter}` : '';
            const { data } = await api.get(`/admin/connect/webhooks${params}`);
            // Server returns a plain array
            setWebhooks(Array.isArray(data) ? data : (data.webhooks || []));
        } catch { toast.error('Error al cargar webhooks'); }
        finally { setLoading(false); }
    }, [platformFilter]);

    useEffect(() => { load(); }, [load]);

    const toggleEvent = (ev) => setForm(f => ({
        ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev],
    }));

    const save = async () => {
        if (!form.platform_id || !form.target_url || form.events.length === 0) {
            toast.error('Completa todos los campos requeridos'); return;
        }
        setSaving(true);
        try {
            await api.post('/admin/connect/webhooks', form);
            toast.success('Webhook creado');
            setShowCreate(false);
            setForm({ platform_id: '', target_url: '', events: [], description: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear webhook'); }
        finally { setSaving(false); }
    };

    const deleteWebhook = async (id) => {
        if (!window.confirm('¿Eliminar este webhook?')) return;
        try { await api.delete(`/admin/connect/webhooks/${id}`); toast.success('Webhook eliminado'); load(); }
        catch { toast.error('Error al eliminar'); }
    };

    const testWebhook = async (id) => {
        setTesting(id);
        try {
            const { data } = await api.post(`/admin/connect/webhooks/${id}/test`);
            // Server returns { ok, latency_ms, status_code }
            const ok = data.ok ?? data.success;
            toast[ok ? 'success' : 'error'](
                ok
                    ? `✅ Test exitoso · ${data.latency_ms}ms`
                    : `❌ Test fallido · HTTP ${data.status_code || '—'} · ${data.latency_ms}ms`
            );
        } catch { toast.error('Error al probar webhook'); }
        finally { setTesting(null); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="font-semibold flex items-center gap-2">
                    <Webhook className="h-4 w-4 text-blue-600" />Gestión de Webhooks
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                    <Select value={platformFilter} onValueChange={setPlatformFilter}>
                        <SelectTrigger className="w-44 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas las plataformas</SelectItem>
                            {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={load}><RefreshCcw className="h-3.5 w-3.5 mr-1" />Actualizar</Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowCreate(true)}>
                        <Plus className="h-3.5 w-3.5 mr-1" />Nuevo webhook
                    </Button>
                </div>
            </div>

            {/* Info box */}
            <Alert>
                <Webhook className="h-4 w-4" />
                <AlertTitle>Webhooks vinculados con Super Admin Connect</AlertTitle>
                <AlertDescription>
                    Los webhooks aquí gestionados usan la infraestructura de Super Admin Connect: HMAC-SHA256, circuit breaker, reintentos automáticos y estadísticas por endpoint.{' '}
                    <Link to="/superadmin/connect" className="text-red-600 hover:underline">Ver panel Connect →</Link>
                </AlertDescription>
            </Alert>

            {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div>
            ) : webhooks.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center">
                    <Webhook className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <div className="font-semibold">Sin webhooks configurados</div>
                    <div className="text-sm text-muted-foreground mt-1 mb-4">Crea un webhook para recibir eventos en tu endpoint.</div>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowCreate(true)}><Plus className="h-3.5 w-3.5 mr-1" />Nuevo webhook</Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {webhooks.map(w => {
                        const plat = platforms.find(p => p.id === w.platform_id);
                        return (
                            <div key={w.id} className="rounded-xl border bg-card p-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant={w.active ? 'default' : 'secondary'} className={w.active ? 'bg-emerald-600' : ''}>
                                                {w.active ? 'Activo' : 'Inactivo'}
                                            </Badge>
                                            <code className="text-sm font-mono truncate">{w.target_url}</code>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{plat?.name || w.platform_id}</div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(w.events || []).map(ev => (
                                                <span key={ev} className="text-[10px] bg-blue-100 text-blue-800 rounded px-1.5 py-0.5 font-mono">{ev}</span>
                                            ))}
                                        </div>
                                        {w.last_fired_at && (
                                            <div className="text-[10px] text-muted-foreground mt-1">
                                                Último envío: {new Date(w.last_fired_at).toLocaleString('es-MX')}
                                                {w.consecutive_failures > 0 && (
                                                    <span className="text-red-600 ml-2">· {w.consecutive_failures} fallos consecutivos</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button size="sm" variant="outline" onClick={() => testWebhook(w.id)} disabled={testing === w.id}>
                                            {testing === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteWebhook(w.id)}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Crear configuración de webhook</DialogTitle>
                        <DialogDescription>El webhook se firmará con HMAC-SHA256 y tendrá reintentos automáticos.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Plataforma vinculada *</Label>
                            <Select value={form.platform_id} onValueChange={v => setForm(f => ({ ...f, platform_id: v }))}>
                                <SelectTrigger><SelectValue placeholder="Seleccionar plataforma…" /></SelectTrigger>
                                <SelectContent>
                                    {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>URL destino *</Label>
                            <Input value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))} placeholder="https://tu-plataforma.com/api/webhooks" />
                        </div>
                        <div>
                            <Label>Eventos *</Label>
                            <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-h-44 overflow-y-auto">
                                {ALL_EVENTS.map(ev => (
                                    <label key={ev} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary rounded p-1">
                                        <Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggleEvent(ev)} />
                                        <code className="font-mono">{ev}</code>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Descripción</Label>
                            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Opcional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                            Crear webhook
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// ── API Keys Tab ──────────────────────────────────────────────────────────────

const ApiKeysTab = ({ platforms }) => {
    const [keys, setKeys] = useState([]);
    const [scopeDefs, setScopeDefs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [newKey, setNewKey] = useState(null); // plain key shown once
    const [form, setForm] = useState({ name: '', scopes: [], platform_id: '', expires_at: '' });
    const [saving, setSaving] = useState(false);
    const [revealedKey, setRevealedKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/api-keys');
            setKeys(data.keys || []);
            setScopeDefs(data.scopes || []);
        } catch { toast.error('Error al cargar claves API'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleScope = (id) => setForm(f => ({
        ...f, scopes: f.scopes.includes(id) ? f.scopes.filter(s => s !== id) : [...f.scopes, id],
    }));

    const createKey = async () => {
        if (!form.name || form.scopes.length === 0) { toast.error('Nombre y al menos un scope son requeridos'); return; }
        setSaving(true);
        try {
            const { data } = await api.post('/admin/api-keys', {
                name: form.name,
                scopes: form.scopes,
                platform_id: form.platform_id || null,
                expires_at: form.expires_at || null,
            });
            setNewKey(data);
            setShowCreate(false);
            setForm({ name: '', scopes: [], platform_id: '', expires_at: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear clave'); }
        finally { setSaving(false); }
    };

    const revokeKey = async (id, name) => {
        if (!window.confirm(`¿Revocar la clave "${name}"? Esta acción no se puede deshacer.`)) return;
        try { await api.delete(`/admin/api-keys/${id}`); toast.success('Clave revocada'); load(); }
        catch { toast.error('Error al revocar'); }
    };

    // Group scopes by group
    const scopeGroups = scopeDefs.reduce((acc, s) => {
        if (!acc[s.group]) acc[s.group] = [];
        acc[s.group].push(s);
        return acc;
    }, {});

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="font-semibold flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-amber-600" />Claves API
                </div>
                <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" onClick={load}><RefreshCcw className="h-3.5 w-3.5 mr-1" />Actualizar</Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowCreate(true)}>
                        <Plus className="h-3.5 w-3.5 mr-1" />Nueva clave
                    </Button>
                </div>
            </div>

            {/* New key reveal banner */}
            {newKey && (
                <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <AlertTitle>Clave creada — guárdala ahora</AlertTitle>
                    <AlertDescription>
                        <p className="text-xs text-muted-foreground mb-2">Esta clave solo se muestra <strong>una vez</strong>. No podrás recuperarla después.</p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 font-mono text-sm bg-white dark:bg-black/20 rounded px-3 py-2 border select-all">
                                {revealedKey === newKey.id ? newKey.plain_key : newKey.key_prefix + '…'}
                            </code>
                            <Button size="sm" variant="outline" onClick={() => setRevealedKey(r => r === newKey.id ? null : newKey.id)}>
                                {revealedKey === newKey.id ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => copyText(newKey.plain_key)}>
                                <Copy className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                        <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => setNewKey(null)}>Cerrar</Button>
                    </AlertDescription>
                </Alert>
            )}

            {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div>
            ) : keys.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center">
                    <KeyRound className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <div className="font-semibold">Sin claves API</div>
                    <div className="text-sm text-muted-foreground mt-1 mb-4">Crea una clave para que aplicaciones externas puedan autenticarse.</div>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowCreate(true)}><Plus className="h-3.5 w-3.5 mr-1" />Nueva clave</Button>
                </div>
            ) : (
                <div className="space-y-2">
                    {keys.map(k => {
                        const plat = platforms.find(p => p.id === k.platform_id);
                        const expired = k.expires_at && new Date(k.expires_at) < new Date();
                        return (
                            <div key={k.id} className={`rounded-xl border bg-card p-4 ${!k.active || expired ? 'opacity-60' : ''}`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold">{k.name}</span>
                                            {k.active && !expired
                                                ? <Badge className="bg-emerald-600 text-[10px]">Activa</Badge>
                                                : expired
                                                    ? <Badge variant="destructive" className="text-[10px]">Expirada</Badge>
                                                    : <Badge variant="secondary" className="text-[10px]">Revocada</Badge>
                                            }
                                            {plat && <Badge variant="outline" className="text-[10px]">{plat.name}</Badge>}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <code className="text-xs font-mono text-muted-foreground">{k.key_prefix}••••••••••••••••</code>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(k.scopes || []).map(s => (
                                                <span key={s} className="text-[10px] bg-secondary rounded px-1.5 py-0.5 font-mono">{s}</span>
                                            ))}
                                        </div>
                                        <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                                            <span>Creada: {new Date(k.created_at).toLocaleDateString('es-MX')}</span>
                                            {k.last_used_at && <span>· Último uso: {new Date(k.last_used_at).toLocaleString('es-MX')}</span>}
                                            {k.expires_at && <span>· Expira: {new Date(k.expires_at).toLocaleDateString('es-MX')}</span>}
                                        </div>
                                    </div>
                                    {k.active && !expired && (
                                        <Button size="sm" variant="ghost" className="text-red-600 shrink-0" onClick={() => revokeKey(k.id, k.name)}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Crear clave API</DialogTitle>
                        <DialogDescription>La clave plain solo se muestra una vez. Guárdala en un lugar seguro.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Nombre *</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Mi integración" />
                        </div>
                        <div>
                            <Label>Plataforma vinculada (opcional)</Label>
                            <Select value={form.platform_id || '__none__'} onValueChange={v => setForm(f => ({ ...f, platform_id: v === '__none__' ? '' : v }))}>
                                <SelectTrigger><SelectValue placeholder="Sin plataforma asociada" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Sin plataforma asociada</SelectItem>
                                    {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Fecha de expiración (opcional)</Label>
                            <Input type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Scopes (permisos) *</Label>
                            {Object.entries(scopeGroups).map(([group, scopes]) => (
                                <div key={group} className="mt-2">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{group}</div>
                                    <div className="space-y-1">
                                        {scopes.map(s => (
                                            <label key={s.id} className="flex items-start gap-2 cursor-pointer hover:bg-secondary rounded p-1.5">
                                                <Checkbox checked={form.scopes.includes(s.id)} onCheckedChange={() => toggleScope(s.id)} className="mt-0.5" />
                                                <div>
                                                    <code className="text-xs font-mono">{s.id}</code>
                                                    <div className="text-[10px] text-muted-foreground">{s.description}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createKey} disabled={saving} className="bg-red-600 hover:bg-red-700">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                            Crear clave
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// ── Integration Tab ───────────────────────────────────────────────────────────

const IntegrationTab = () => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/api-spec/connect-summary')
            .then(({ data }) => setSummary(data))
            .catch(() => toast.error('Error al cargar resumen'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</div>;

    const platforms = summary?.platforms || [];

    return (
        <div className="space-y-5">
            <div className="font-semibold flex items-center gap-2">
                <ArrowLeftRight className="h-4 w-4 text-red-600" />
                Integración con Super Admin Connect
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Plataformas vinculadas', value: platforms.length, icon: Network, color: '' },
                    { label: 'Webhooks activos',       value: summary?.active_webhooks,  icon: Webhook,  color: 'text-blue-600' },
                    { label: 'Claves API activas',     value: summary?.active_api_keys,  icon: KeyRound, color: 'text-amber-600' },
                    { label: 'Módulos integrados',     value: summary?.active_modules,   icon: PlugZap,  color: 'text-purple-600' },
                ].map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="rounded-xl border bg-card p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                            <Icon className="h-3.5 w-3.5" />
                            <span className="text-[11px]">{label}</span>
                        </div>
                        <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
                    </div>
                ))}
            </div>

            {/* Platform code */}
            {summary?.platform_code && (
                <div className="rounded-xl border bg-card p-4">
                    <div className="text-sm font-semibold mb-1 flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-emerald-600" />Tu código de plataforma
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="font-mono text-lg tracking-widest bg-secondary px-4 py-2 rounded-lg flex-1 select-all">
                            {summary.platform_code}
                        </code>
                        <Button size="icon" variant="ghost" onClick={() => copyText(summary.platform_code)}><Copy className="h-4 w-4" /></Button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Comparte este código con plataformas que quieran vincularse contigo.</div>
                </div>
            )}

            {/* Platform list */}
            {platforms.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center">
                    <Network className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <div className="font-semibold">Sin plataformas vinculadas</div>
                    <div className="text-sm text-muted-foreground mt-1 mb-4">Vincula plataformas desde el panel Super Admin Connect.</div>
                    <Button asChild size="sm" className="bg-red-600 hover:bg-red-700">
                        <Link to="/superadmin/connect"><Network className="h-3.5 w-3.5 mr-1" />Ir a Super Admin Connect</Link>
                    </Button>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="text-sm font-semibold">Plataformas vinculadas</div>
                    {platforms.map(p => (
                        <div key={p.id} className="rounded-xl border bg-card p-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium truncate">{p.name}</span>
                                    <Badge variant="outline" className={`text-[10px] ${p.status === 'connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                        {p.status}
                                    </Badge>
                                    {p.reverse_link_status === 'ok' && (
                                        <Badge className="bg-blue-100 text-blue-800 text-[10px]">Reverse ✓</Badge>
                                    )}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    <code className="font-mono">{p.platform_code}</code>
                                    {p.uptime_pct !== undefined && ` · Uptime ${p.uptime_pct}%`}
                                </div>
                            </div>
                            <Button asChild size="sm" variant="ghost">
                                <a href={p.url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>API de Connect vinculada</AlertTitle>
                <AlertDescription>
                    Los webhooks y claves API gestionados aquí comparten la infraestructura de Super Admin Connect.
                    Cambios en plataformas vinculadas se reflejan en tiempo real.{' '}
                    <Link to="/superadmin/connect" className="text-red-600 hover:underline">Ir al panel Connect →</Link>
                </AlertDescription>
            </Alert>
        </div>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminApiConnect() {
    const [platforms, setPlatforms] = useState([]);

    useEffect(() => {
        api.get('/admin/connect/platforms').then(({ data }) => setPlatforms(data || [])).catch(() => {});
    }, []);

    return (
        <div className="space-y-6 pb-12">
            {/* Header */}
            <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center shrink-0">
                    <Code2 className="h-5 w-5 text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-bold">API Connect</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Documentación de la API, gestión de webhooks y claves de acceso vinculadas a Super Admin Connect.
                    </p>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                    <Button asChild variant="outline" size="sm">
                        <Link to="/superadmin/connect"><Network className="h-3.5 w-3.5 mr-1" />Super Admin Connect</Link>
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="docs">
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="docs"><BookOpen className="h-3.5 w-3.5 mr-1" />Documentación</TabsTrigger>
                    <TabsTrigger value="webhooks"><Webhook className="h-3.5 w-3.5 mr-1" />Webhooks</TabsTrigger>
                    <TabsTrigger value="keys"><KeyRound className="h-3.5 w-3.5 mr-1" />Claves API</TabsTrigger>
                    <TabsTrigger value="integration"><ArrowLeftRight className="h-3.5 w-3.5 mr-1" />Integración</TabsTrigger>
                </TabsList>

                <TabsContent value="docs" className="mt-4"><DocsTab /></TabsContent>
                <TabsContent value="webhooks" className="mt-4"><WebhooksTab platforms={platforms} /></TabsContent>
                <TabsContent value="keys" className="mt-4"><ApiKeysTab platforms={platforms} /></TabsContent>
                <TabsContent value="integration" className="mt-4"><IntegrationTab /></TabsContent>
            </Tabs>
        </div>
    );
}
